<template>
	<div>
		<!-- Placeholder quando i cookie non sono accettati -->
		<div v-if="!hasConsent" class="map-placeholder">
			<div class="placeholder-content">
				<div class="placeholder-icon">📍</div>
				<p>Per visualizzare la mappa è necessario accettare i cookie di funzionalità.</p>
				<a 
					href="https://www.comune.lucca.it/progetti/cookie-policy/" 
					target="_blank"
					class="cookie-link"
				>
					Maggiori informazioni
				</a>
				<button @click="reloadPage" class="accept-btn">
					Accetta e visualizza mappa
				</button>
			</div>
		</div>

		<!-- Mappa quando i cookie sono accettati -->
		<div v-else class="map" ref="googleMap"></div>
		
		<template v-if="Boolean(this.google) && Boolean(this.map) && hasConsent">
			<slot :google="google" :map="map" />
		</template>
	</div>
</template>

<style lang="scss" scoped>
.map {
	height: 536px;
}

.map-placeholder {
	height: 536px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border: 2px dashed #ccc;
	border-radius: 4px;
	
	.placeholder-content {
		text-align: center;
		padding: 40px 20px;
		max-width: 400px;
		
		.placeholder-icon {
			font-size: 64px;
			margin-bottom: 20px;
			opacity: 0.5;
		}
		
		p {
			color: #616161;
			font-size: 16px;
			line-height: 1.5;
			margin: 0 0 15px 0;
		}
		
		.cookie-link {
			display: inline-block;
			color: #497dd6;
			text-decoration: underline;
			font-size: 14px;
			margin-bottom: 20px;
			
			&:hover {
				color: #3461a8;
			}
		}
		
		.accept-btn {
			display: block;
			width: 100%;
			padding: 12px 24px;
			background-color: #497dd6;
			color: white;
			border: none;
			border-radius: 4px;
			font-size: 14px;
			font-weight: 600;
			cursor: pointer;
			transition: background-color 0.2s;
			
			&:hover {
				background-color: #3461a8;
			}
		}
	}
}

.search-google-maps {
	width: 40%;
}
</style>

<script>
import GoogleMapsApiLoader from "google-maps-api-loader";
import { mapGetters } from 'vuex';

export default {
	name: "GoogleMapLoader",
	props: ["markers", "position"],
	data() {
		return {
			apiKey: "AIzaSyAP7K3KFeEEvNA8owcRzs5vYv4BVDjpVgE",
			google: null,
			items: null,
			map: null,
			mapConfig: {
				zoom: 6,
				styles: [
					{
						elementType: "geometry",
						stylers: [
							{
								color: "#f5f5f5",
							},
						],
					},
					{
						elementType: "labels.icon",
						stylers: [
							{
								visibility: "off",
							},
						],
					},
					{
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#616161",
							},
						],
					},
					{
						elementType: "labels.text.stroke",
						stylers: [
							{
								color: "#f5f5f5",
							},
						],
					},
					{
						featureType: "administrative.land_parcel",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#bdbdbd",
							},
						],
					},
					{
						featureType: "poi",
						elementType: "geometry",
						stylers: [
							{
								color: "#eeeeee",
							},
						],
					},
					{
						featureType: "poi",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#757575",
							},
						],
					},
					{
						featureType: "poi.park",
						elementType: "geometry",
						stylers: [
							{
								color: "#e5e5e5",
							},
						],
					},
					{
						featureType: "poi.park",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#9e9e9e",
							},
						],
					},
					{
						featureType: "road",
						elementType: "geometry",
						stylers: [
							{
								color: "#ffffff",
							},
						],
					},
					{
						featureType: "road.arterial",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#757575",
							},
						],
					},
					{
						featureType: "road.highway",
						elementType: "geometry",
						stylers: [
							{
								color: "#dadada",
							},
						],
					},
					{
						featureType: "road.highway",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#616161",
							},
						],
					},
					{
						featureType: "road.local",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#9e9e9e",
							},
						],
					},
					{
						featureType: "transit.line",
						elementType: "geometry",
						stylers: [
							{
								color: "#e5e5e5",
							},
						],
					},
					{
						featureType: "transit.station",
						elementType: "geometry",
						stylers: [
							{
								color: "#eeeeee",
							},
						],
					},
					{
						featureType: "water",
						elementType: "geometry",
						stylers: [
							{
								color: "#c9c9c9",
							},
						],
					},
					{
						featureType: "water",
						elementType: "labels.text.fill",
						stylers: [
							{
								color: "#9e9e9e",
							},
						],
					},
				],
			},
			selectedMunicipality: null,
			search: null,
		};
	},
	computed: {
		...mapGetters(['hasCookieConsent']),
		hasConsent() {
			return this.hasCookieConsent('functionality');
		}
	},
	watch: {
		// Monitora i cambiamenti nei consensi
		hasConsent(newVal) {
			if (newVal && !this.map) {
				this.loadMap();
			}
		}
	},
	async mounted() {
		// Carica la mappa solo se c'è il consenso
		if (this.hasConsent) {
			await this.loadMap();
		}
	},
	methods: {
		async loadMap() {
			this.mapConfig.center = this.position;
			const googleMapApi = await GoogleMapsApiLoader({
				libraries: ["places"],
				apiKey: this.apiKey,
			});
			this.google = googleMapApi;
			
			// Aspetta il DOM prima di inizializzare
			this.$nextTick(() => {
				this.initializeMap();
			});
		},
		initializeMap() {
			const mapContainer = this.$refs.googleMap;
			if (!mapContainer) return;
			
			this.map = new this.google.maps.Map(mapContainer, this.mapConfig);
			
			if (this.markers && this.markers.length > 1) {
				let bounds = new this.google.maps.LatLngBounds();
				this.markers.map((marker) => {
					bounds.extend(marker.position);
				});
				this.map.fitBounds(bounds);
			} else if (this.markers && this.markers.length === 1) {
				this.map.setZoom(16);
				this.map.setCenter(this.markers[0].position);
			}
		},
		reloadPage() {
			// Accetta i cookie functionality e ricarica
			this.$store.dispatch('updateCookieConsent', { 
				type: 'functionality', 
				value: true 
			});
			window.location.reload();
		}
	},
};
</script>